import { useEffect } from 'react';

import { useTranslation } from 'react-i18next';
import useSWR from 'swr';

import Card from '~components/containers/card';
import TwoLineGrid from '~components/data/twoLineGrid';
import ResourceGroupService from '~features/resourceGroups/services/resourceGroupService';
import { formatScheduledActionCronExpression } from '~features/scheduledActions/interfaces/scheduledAction';
import ScheduledActionsService from '~features/scheduledActions/services/scheduledActionsService';
import { getService } from '~services/serviceFactory';
import { unwrapCronExpression } from '~utils/cronExpressionFormatter';
import { formatDateTime } from '~utils/dateUtils';
import { defaultPlaceholder } from '~utils/stringUtils';

interface ScheduledActionInformationCardProps {
	scheduledActionId?: string | undefined;
	organisationId: string | undefined;
	isLoading?: boolean;
}

const scheduledActionService = getService(ScheduledActionsService);
const resourceGroupService = getService(ResourceGroupService);

const ScheduledActionInformationCard = ({
	scheduledActionId,
	organisationId,
}: ScheduledActionInformationCardProps) => {
	const { t } = useTranslation();

	const {
		data: scheduledActionData,
		error: scheduledActionError,
		isLoading: scheduledActionIsLoading,
	} = useSWR(
		scheduledActionService.getScheduledActionByIdCacheKey(scheduledActionId, organisationId),
		([_, id, orgId]) => {
			if (id == undefined) return;
			return scheduledActionService.getScheduledActionById(id, orgId);
		}
	);

	const isScheduledActionLoading = scheduledActionIsLoading && scheduledActionData == undefined;
	const hasScheduledActionError = scheduledActionError != undefined;

	const resourceGroupId = scheduledActionData?.resourceGroupId?.toString();

	const {
		data: resourceGroupData,
		error: resourceGroupError,
		isLoading: resourceGroupIsLoading,
		mutate: revalidateResourceGroup,
	} = useSWR(
		resourceGroupService.getResourceGroupByIdCacheKey(resourceGroupId, organisationId),
		([_, id]) => (id ? resourceGroupService.getResourceGroupById(id, organisationId) : null),
		{
			revalidateOnMount: true,
		}
	);

	const isResourceGroupLoading = resourceGroupIsLoading && resourceGroupData == undefined;
	const hasResourceGroupError = resourceGroupError != undefined;

	// Resource group data revalidation
	useEffect(() => {
		if (resourceGroupId) {
			revalidateResourceGroup();
		}
	}, [resourceGroupId, revalidateResourceGroup]);

	const hasError = hasScheduledActionError || hasResourceGroupError;
	const isInitialLoading = isScheduledActionLoading || isResourceGroupLoading;
	const formattedScheduleLabel = formatScheduledActionCronExpression(scheduledActionData, t);
	const scheduleTooltipText =
		scheduledActionData?.cronExpression ?
			unwrapCronExpression(scheduledActionData?.cronExpression)
		:	defaultPlaceholder;

	return (
		<Card title={t('schedule.fields.scheduleDetails', { ns: 'entities' })}>
			<TwoLineGrid
				isLoading={isInitialLoading}
				showError={hasError}
				twoLineItems={[
					{
						primary: t('general.id', { ns: 'entities' }),
						secondary: scheduledActionData?.id.toString(),
					},
					{
						primary: t('schedule.labels.single', { ns: 'entities' }),
						secondary: formattedScheduleLabel,
						tooltipText: scheduleTooltipText || defaultPlaceholder,
					},
					{
						primary: t('start', { ns: 'date' }),
						secondary: (() => {
							const date = scheduledActionData?.start ?? scheduledActionData?.dateTime;
							return date ? formatDateTime(date) : defaultPlaceholder;
						})(),
					},
					{
						primary: t('end', { ns: 'date' }),
						secondary: (() => {
							const date = scheduledActionData?.end;
							return date ? formatDateTime(date) : defaultPlaceholder;
						})(),
					},
					{
						primary: t('resourceGroup.labels.single', { ns: 'entities' }),
						secondary: resourceGroupData?.name ?? defaultPlaceholder,
					},
				]}
			/>
		</Card>
	);
};

export default ScheduledActionInformationCard;
